import {mapValues} from 'lodash';
import {Subscription} from 'rxjs';
import {filter, first, startWith} from 'rxjs/operators';
import uuid from 'uuid-random';

import {FrontWindow} from '../../../libs/shared/helpers/src/browser/browserHelpers';
import {isDevelopment} from '../../../libs/shared/helpers/src/environment/envHelpers';
import {TaskSourcesEnum} from '../../../libs/shared/helpers/src/instrumentation/eventLoopPerformanceHelpers';
import {FrontEvent, Handler} from '../../../libs/shared/helpers/src/react/reactHelpers';
import {isDefined} from '../../../libs/shared/helpers/src/typescript/typeHelpers';
import {AppStore} from '../../../src/store/appStore';
import {markStep} from '../../helpers/performanceHelpers';
import {fromVisibilityEvent, isHidden} from '../../rx/visibility';
import {logger} from '../logger/logger';
import {LogMessageContextValue} from '../logger/loggerCommon';
import {interactionSampleRates} from './interactionPerformanceConstants';
import {
  PerformanceInteractionStepsEnum,
  PerformanceInteractionTypesEnum,
} from './interactionPerformanceTypes';
import {performance} from './performanceClient';
import {getHasPerformanceLogging, getHasPerformanceMetricLogging} from './performanceMetricSelectors';

/*
 * Constants.
 */

const shouldSampleAllEventsInDev = false;
const interactionTimeoutDuration = 10 * 60 * 1000;
export const interactionPerformanceLogger = logger.getLogger('interaction-performance');

/*
 * Types.
 */

interface OngoingInteraction {
  startTime: number;
  type: PerformanceInteractionTypesEnum;
  stepTimes: Partial<{[key in PerformanceInteractionStepsEnum]: number}>;
  metadata: {[key: string]: LogMessageContextValue};
  logLatency: number;
  window: FrontWindow;
  cleanup: Handler;
}

type WindowProvider = (object: Element | EventTarget | null) => FrontWindow | undefined;
export type UIInteractionEvent = Pick<FrontEvent, 'timeStamp' | 'target'>;

/*
 * State.
 */

const ongoingInteractions = new Map<string, OngoingInteraction>();
let isLoggingEnabled = false;
let isPerformanceMetric = false;
let findWindow: WindowProvider;

/*
 * Api.
 */

/**  Subscribes to the performance interaction feature flag and enables/disables logging dynamically. */
export function setupInteractionPerformanceLogging(store: AppStore, windowProvider: WindowProvider) {
  const updateLoggingState = () => {
    const state = store.getState();
    isPerformanceMetric = getHasPerformanceMetricLogging(state);
    isLoggingEnabled = getHasPerformanceLogging(state);
  };

  findWindow = windowProvider;
  store.subscribe(updateLoggingState);
  updateLoggingState();
}

/**
 * Attempts to start logging a performance interaction.
 * @param {UIInteractionEvent} event An event so we can backdate to the true start time.
 * @param {PerformanceInteractionTypesEnum} interactionType The type of interaction that occurred.
 * @param {boolean} shouldEndAutomatically Should the interaction be considered finished on the next animation frame.
 * @returns {string | undefined} A string if this interaction will be logged or undefined.
 */
export function startPerformanceInteraction(
  event: UIInteractionEvent,
  interactionType: PerformanceInteractionTypesEnum,
  shouldEndAutomatically: boolean = false,
) {
  if (!shouldLogInteraction(interactionType)) {
    return undefined;
  }

  const window = findWindow(event.target);
  if (!window) {
    interactionPerformanceLogger.warn('Unable to find window for event', {
      interaction_type: interactionType,
    });
    return undefined;
  }

  const now = performance.now();
  const startTime = event.timeStamp;
  const performanceInteractionId = uuid();
  markStep(performance, `Interaction ${performanceInteractionId} Started`);

  const timerId = window.setInstrumentedTimeout(() => {
    // Interaction didn't end.
    const interaction = ongoingInteractions.get(performanceInteractionId);
    interactionPerformanceLogger.warn('Did not end performance interaction', {
      interaction_type: interactionType,
      completed_steps: Object.keys(interaction?.stepTimes || {}),
      metadata: interaction?.metadata,
    });

    interaction?.cleanup();
  }, interactionTimeoutDuration);

  const subscription = new Subscription();

  const cleanup = () => {
    window.clearTimeout(timerId);
    subscription.unsubscribe();
    ongoingInteractions.delete(performanceInteractionId);
  };

  ongoingInteractions.set(performanceInteractionId, {
    startTime,
    type: interactionType,
    stepTimes: {},
    metadata: {},
    logLatency: now - startTime,
    window,
    cleanup,
  });

  subscription.add(
    fromVisibilityEvent(window)
      .pipe(
        startWith(isHidden(window)),
        filter((isWindowHidden) => Boolean(isWindowHidden)),
        first(),
      )
      .subscribe(() => cancelPerformanceInteraction(performanceInteractionId)),
  );

  // Automatically record the UI render step.
  markPerformanceInteractionStepOnAnimationFrame(
    performanceInteractionId,
    PerformanceInteractionStepsEnum.UI_UPDATE,
    shouldEndAutomatically,
  );

  return performanceInteractionId;
}

/**
 * Attempts to start logging a performance interaction that will automatically be marked as completed on the next animation frame.
 * @param {UIInteractionEvent} event An event so we can backdate to the true start time.
 * @param {PerformanceInteractionTypesEnum} interactionType The type of interaction that occurred.
 */
export function startSimplePerformanceInteraction(
  event: UIInteractionEvent,
  interactionType: PerformanceInteractionTypesEnum,
) {
  startPerformanceInteraction(event, interactionType, true);
}

/**
 * Adds a step to a performance interaction.
 * @param {string} performanceInteractionId The id of the interaction.
 * @param {PerformanceInteractionStepsEnum} step The step that was just finished.
 * @param {boolean} isFinalStep Should we finish the interaction now.
 * @param {DOMHighResTimeStamp} timeOverride The timestamp of the step. Will use `performance.now()` if undefined.
 */
export function markPerformanceInteractionStep(
  performanceInteractionId: string,
  step: PerformanceInteractionStepsEnum,
  isFinalStep: boolean = false,
  timeOverride?: DOMHighResTimeStamp,
) {
  const interaction = ongoingInteractions.get(performanceInteractionId);
  if (!interaction) {
    // Adding a step to an interaction that has already ended.
    return;
  }

  const now = interaction.window.performance.now();
  const stepTime = timeOverride ?? now;

  if (stepTime > now) {
    // Adding a step in the future is not allowed
    interactionPerformanceLogger.warn('Invalid step time', {
      interaction_type: interaction.type,
      completed_steps: Object.keys(interaction?.stepTimes || {}),
      metadata: interaction?.metadata,
    });
    return;
  }

  if (interaction.stepTimes[step]) {
    // This step has already been recorded.
    return;
  }

  markStep(performance, buildMarkStepPerformanceLabel(interaction.type, step), {startTime: timeOverride});
  interaction.stepTimes[step] = stepTime;

  if (isFinalStep) {
    finishPerformanceInteraction(performanceInteractionId);
  }
}

/**
 * Adds a step to a performance interaction on the next animation frame.
 * @param {string} performanceInteractionId The id of the interaction.
 * @param {PerformanceInteractionStepsEnum} step The step that was just finished.
 * @param {boolean} isFinalStep Should we finish the interaction after this step.
 */
export function markPerformanceInteractionStepOnAnimationFrame(
  performanceInteractionId: string | undefined,
  step: PerformanceInteractionStepsEnum,
  isFinalStep: boolean = false,
) {
  if (!performanceInteractionId) {
    return;
  }

  const interaction = ongoingInteractions.get(performanceInteractionId);
  if (!interaction) {
    // Adding a step to an interaction that has already ended.
    return;
  }

  interaction.window.requestInstrumentedAnimationFrame(
    () => markPerformanceInteractionStep(performanceInteractionId, step, isFinalStep),
    {source: TaskSourcesEnum.LOGGING},
  );
}

/**
 * Adds metadata to a performance interaction.
 * @param {string} performanceInteractionId The id of the interaction.
 * @param {string} key The metadata key name.
 * @param {value} ContextValue The metadata value.
 */
export function addPerformanceInteractionMetadata(
  performanceInteractionId: string,
  key: string,
  value: LogMessageContextValue,
) {
  const interaction = ongoingInteractions.get(performanceInteractionId);
  if (!interaction) {
    return;
  }

  interaction.metadata[key] = value;
}

/**
 * Finishes a performance interaction and sends the log to the server.
 * @param {string} performanceInteractionId The id of the interaction.
 */
export function finishPerformanceInteraction(performanceInteractionId: string) {
  const interaction = ongoingInteractions.get(performanceInteractionId);
  if (!interaction) {
    // Finishing an interaction that has already ended.
    return;
  }

  const {startTime, stepTimes, cleanup, type, metadata, logLatency, window} = interaction;
  const now = window.performance.now();
  performance.measure(`Interaction ${type}`, `Interaction ${performanceInteractionId} Started`);
  performance.clearMarks(`Interaction ${performanceInteractionId} Started`);
  cleanup();

  const duration = now - startTime;
  if (duration < 0) {
    return;
  }

  // Compute difference from the start time and send to DataDog.
  interactionPerformanceLogger.info('Measured performance interaction', {
    duration,
    step_durations: mapValues(stepTimes, (stepTime) =>
      isDefined(stepTime) ? stepTime - startTime : undefined,
    ),
    interaction_type: type,
    log_latency: logLatency,
    start_time: startTime,
    now,
    metadata,
    is_performance_metric: isPerformanceMetric,
  });
}

/**
 * Cancels a performance interaction. Useful if we want to start a new interaction or the user aborted
 * the task. This log is also sent to the server.
 * @param {string | undefined} performanceInteractionId The id of the interaction.
 */
export function cancelPerformanceInteraction(performanceInteractionId: string | undefined) {
  if (!performanceInteractionId) {
    return;
  }

  const interaction = ongoingInteractions.get(performanceInteractionId);
  if (!interaction) {
    // Canceling an interaction that has already ended.
    return;
  }

  const {startTime, stepTimes, cleanup, type, metadata, logLatency, window} = interaction;
  const now = window.performance.now();
  markStep(performance, `Interaction ${type} Canceled`);
  cleanup();

  interactionPerformanceLogger.info('Cancelled performance interaction', {
    duration: now - startTime,
    interaction_type: type,
    completed_steps: Object.keys(stepTimes),
    log_latency: logLatency,
    metadata,
  });
}

/*
 * Helpers.
 */

function shouldLogInteraction(interactionType: PerformanceInteractionTypesEnum) {
  if (!isLoggingEnabled) {
    return false;
  }

  const sampleRate = interactionSampleRates[interactionType];
  if (isDevelopment() && shouldSampleAllEventsInDev) {
    return true;
  }

  return Math.random() < sampleRate;
}

function buildMarkStepPerformanceLabel(
  type: PerformanceInteractionTypesEnum,
  step: PerformanceInteractionStepsEnum,
) {
  return `Interaction step ${step} for ${type}`;
}
